import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FilesTypeEnum } from '@app/ips/models/enum/files-type.enum';
import {
  IScreeningResultResponse,
  IScreeningDivergenceReportResponse,
} from '@app/ips/screening/models';
import { HttpService } from '@app/shared/services/http/http.service';
import { UrlService } from '@app/shared/services/url/url.service';
import Utils from '@app/shared/utils/utils';
import { Store } from '@ngxs/store';
import {
  BehaviorSubject,
  Observable,
  catchError,
  map,
  tap,
  forkJoin,
  throwError,
  lastValueFrom,
  retry,
  shareReplay,
} from 'rxjs';
import { SetHasDivergenceReport } from './screening.actions';

@Injectable()
export class ScreeningService {
  progress = new BehaviorSubject({
    fileName: null,
    total: 0,
    current: 0,
    successMessage: '',
    errorMessage: '',
    error: false,
  });

  // TODO: REVISAR
  constructor(
    private _httpService: HttpService,
    private _urlService: UrlService,
    private _store: Store
  ) {}

  getCountStatusTrade(idTrade: number): Observable<number> {
    const url = this._urlService.getUrl('COLLECT.countStatusTrade', { idTrade });
    return this._httpService.get(url, null).pipe(catchError(this.errorMgmt));
  }

  private _getUserAnswer(idTrade: string): Observable<any[]> {
    const url = this._urlService.getUrl('RECERTIFICATION.getUserAnswer', { idTrade });
    return this._httpService.get(url, null).pipe(
      map((data: any) => data.AnswerCustomer),
      map((answers) =>
        answers.map((answer: { id: string; value: boolean }) => ({
          idQuestion: answer.id,
          customerAnswer: answer.value,
        }))
      ),
      catchError(this.errorMgmt)
    );
  }

  private _getQuestions(
    idOperation: string,
    idsubcategory: string,
    IdTrade: string
  ): Observable<any[]> {
    const url = this._urlService.getUrl('RECERTIFICATION.getQuestions', {
      idOperation,
      idsubcategory,
      IdTrade,
    });

    const buildDivergenceReasons = (divergenceReasons: any[]) => {
      if (!!divergenceReasons.length) {
        return divergenceReasons.map((item) => ({
          idReason: item.idDivergenceReason,
          description: item.reason,
          idQuestion: item.idQuestion,
          diagnostic: item.diagnostic,
        }));
      }
      return [];
    };

    const buildDefaultAnswer = (
      defaultAnswer: { idQuestion: string; idAnswer: string; value: boolean },
      idQuestion: string,
      answer: boolean
    ) => {
      if (idQuestion === defaultAnswer.idQuestion && answer === defaultAnswer.value) {
        return true;
      }
      return null;
    };

    return this._httpService.get(url, null).pipe(
      map((data: any) => ({
        lastAnswer: data.lastAnswer,
        questions: data.operation.subCategorys[0].checklist.questions,
      })),
      map((response) =>
        response.questions.map((question) => ({
          idQuestion: question.id,
          description: question.description,
          divergenceReasons: buildDivergenceReasons(question.divergencesReason),
          sequence: question.sequence,
          editEnabled: question.enabled,
          answers: question.answers.map((answer) => ({
            idQuestion: answer.idQuestion,
            value: answer.value,
            description: answer.value ? 'Sim' : 'Não',
            enabled: answer.enabled,
            defaultAnswer: buildDefaultAnswer(response.lastAnswer, question.id, answer.value),
          })),
          customerAnswer: false,
        }))
      ),
      tap((questions: any[]) => {
        const hasdivergenceReasons = questions
          .map((q: { divergenceReasons: any[] }) => q.divergenceReasons)
          .some((dr) => !!dr.length);
        if (hasdivergenceReasons) {
          this._store.dispatch(new SetHasDivergenceReport(true));
        }
      }),
      catchError(this.errorMgmt)
    );
  }

  getQuestions(idOperation: string, idsubcategory: string, idTrade: string) {
    const answers$ = this._getUserAnswer(idTrade);
    const questions$ = this._getQuestions(idOperation, idsubcategory, idTrade);

    return forkJoin([answers$, questions$]).pipe(
      map(([answers, questions]) =>
        answers
          .map((answer) => {
            const correspondingQuestion = questions.find(
              (question) => question.idQuestion === answer.idQuestion
            );
            if (correspondingQuestion) {
              return {
                ...correspondingQuestion,
                customerAnswer: answer.customerAnswer,
              };
            }
            return null;
          })
          .filter((combinedList) => combinedList !== null)
      ),
      catchError(this.errorMgmt)
    );
  }

  getTradesByIdTrade(idTrade: string): Observable<any> {
    const url = this._urlService.getUrl('COLLECT.tradesByIdTrade', { idTrade });
    return this._httpService.get(url, null).pipe(catchError(this.errorMgmt));
  }

  getTradesFilterScreening(filter: string) {
    const url = this._urlService.getUrl('COLLECT.getTradesFilterScreening', { filter });
    return this._httpService.get(url, null).pipe(catchError(this.errorMgmt));
  }

  getCheckListResult(idTrade: string): Observable<IScreeningResultResponse> {
    const url = this._urlService.getUrl('COLLECT.getCheckListResult', { idTrade });
    return this._httpService.get(url, null).pipe(catchError(this.errorMgmt));
  }

  getDivergenceReport(idTrade: string): Observable<IScreeningDivergenceReportResponse> {
    const url = this._urlService.getUrl('COLLECT.getDivergenceReport', { idTrade });
    return this._httpService.get(url, null).pipe(catchError(this.errorMgmt));
  }

  saveAnswers(idTrade: string, payload: any) {
    const url = this._urlService.getUrl('COLLECT.saveAnswers', { idTrade });
    return this._httpService.post(url, payload).pipe(catchError(this.errorMgmt));
  }

  setStatusScreening(idTrade: string, screeningStep: string) {
    const httpOptions = { 'Content-Type': 'application/json' };
    const url = this._urlService.getUrl('COLLECT.updateScreeningStep');
    return this._httpService
      .post(url, { idTrade, screeningStep }, httpOptions)
      .pipe(catchError(this.errorMgmt));
  }

  resetScreening(idTrade: string) {
    const url = this._urlService.getUrl('COLLECT.resetScreening');
    return this._httpService.post(url, { idTrade }).pipe(catchError(this.errorMgmt));
  }

  updateAdditionalInformation(payload: any) {
    const url = this._urlService.getUrl('COLLECT.updateAdditionalInformation');

    return this._httpService
      .put(url, payload)
      .pipe(catchError((error: any) => throwError(() => error)));
  }

  isImeiBipedAvailable(imei: string, idTrade: string) {
    const payload = { imei, idtradeExcepts: idTrade };
    const url = this._urlService.getUrl('COLLECT.getImeiAvailability', payload);
    return this._httpService.get(url, payload).pipe(
      catchError((errorRepsonse) => {
        if (errorRepsonse.errors) {
          return throwError(() => new Error(errorRepsonse.errors[0]));
        }
        return throwError(() => errorRepsonse);
      })
    );
  }

  // Todo: Teste para exibir progresso no upload
  errorMgmt(error: HttpErrorResponse) {
    debugger;
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }

  directUpload(payload: any) {
    const url = this._urlService.getUrl('COLLECT.uploadFileChunk');
    const headers = { 'Content-Type': 'application/json' };
    return this._httpService.post(url, payload, headers);
  }

  upload(file: any, successMessage = '', errorMessage = '') {
    return new Promise(async (resolve, reject) => {
      const MB = 30; // = 30MB real
      const chunkSize = 1024 * 1024 * MB;
      let chunks = [];
      const { videoName, idTrade, idScreeningStep, blobRec, fileType } = file;

      this.progress.next({
        fileName: videoName,
        total: 0,
        current: 0,
        successMessage,
        errorMessage,
        error: false,
      });

      if (typeof blobRec === 'string') {
        chunks = await this._chunkBase64File(blobRec);
      } else {
        chunks = await this._chunk(blobRec, chunkSize);
      }

      const idGUID = Utils.newGuid();

      this._uploadFile(
        {
          idGUID,
          nameFile: videoName,
          tradeId: idTrade,
          idScreeningStep,
          chunks,
          fileType,
          successMessage,
          errorMessage,
        },
        resolve,
        reject
      );
    });
  }

  private async _uploadFile(
    {
      idGUID,
      nameFile,
      tradeId,
      idScreeningStep,
      chunks,
      fileType,
      successMessage,
      errorMessage,
    }: {
      idGUID: string;
      nameFile: string;
      tradeId: string;
      idScreeningStep: number;
      chunks: any[];
      fileType: string;
      successMessage: string;
      errorMessage: string;
    },
    resolve: { (value: unknown): void; (arg: unknown): void },
    reject: { (reason?: any): void; (arg: any): void }
  ) {
    let index = 0;
    const getFileTypeCode = (mimeType: string) => {
      const part = mimeType.split('/').shift();
      switch (part) {
        case 'image':
          return FilesTypeEnum.Image;
        case 'video':
          return FilesTypeEnum.Video;
        default:
          return FilesTypeEnum.Pdf;
      }
    };
    for await (const chunk of chunks) {
      index++;
      const payload = {
        tradeId,
        idScreeningStep,
        nameFile,
        type: getFileTypeCode(fileType),
        file: chunk,
        lastPart: chunks.length,
        currentPart: index,
        keyMidia: idGUID,
      };

      try {
        await this._uploadFileChunk(payload);
        this.progress.next({
          fileName: nameFile,
          total: chunks.length,
          current: index,
          successMessage,
          errorMessage,
          error: false,
        });
      } catch (error) {
        this.progress.next({
          fileName: nameFile,
          total: 0,
          current: 0,
          successMessage,
          errorMessage,
          error: true,
        });
        reject(error);
      }
    }

    resolve('Upload finalizado com sucesso.');
  }

  private _chunkBase64File(base64File: string): Promise<any[]> {
    return new Promise((resolve) => {
      resolve([base64File]);
    });
  }

  private _chunk(file: File, maxBytes: number): Promise<any[]> {
    return new Promise((resolve) => {
      const result: any = [];

      let startPointer = 0;
      const endPointer = file.size;

      while (startPointer < endPointer) {
        const newStartPointer = startPointer + maxBytes;
        result.push(file.slice(startPointer, newStartPointer));
        startPointer = newStartPointer;
      }

      resolve(result);
    });
  }

  private _blobToBase64(blob: Blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  private _uploadFileChunk(payload: any) {
    return new Promise(async (resolve, reject) => {
      const url = this._urlService.getUrl('COLLECT.uploadFileChunk');
      const headers = { 'Content-Type': 'application/json' };
      const file =
        typeof payload.file === 'string' ? payload.file : await this._blobToBase64(payload.file);
      const data = {
        tradeId: payload.tradeId,
        idScreeningStep: payload.idScreeningStep,
        nameFile: payload.nameFile,
        lastPart: payload.lastPart,
        currentPart: payload.currentPart,
        type: payload.type,
        keyMidia: payload.keyMidia,
        file,
      };

      this._executePost(url, data, { headers })
        .then((offset) => {
          resolve(offset);
        })
        .catch((err) => reject(err));
    });
  }

  private async _executePost(url: string, payload: any, requestHeaders: any) {
    return lastValueFrom(
      this._httpService.post(url, payload, requestHeaders).pipe(
        retry(3),
        shareReplay(),
        catchError((error) => this.errorMgmt(error))
      )
    );
  }
}
